import React from 'react';
import { Stack, Typography } from '@mui/material';
import { ExternalLink } from 'lucide-react';
import { Button as ButtonUI } from '@repo/ui';
import { ProductDetailsPanel } from 'pages/CertificatePage/components/ProductDetailsPanel';
import { BrandCustomFields } from 'pages/CertificatePage/components/BrandCustomFields';
import OwnerProvenanceCard from 'components/pages/certificates/productPage/OwnerProvenanceCard';
import { getOwner } from 'utils/parsers';
import { font, space } from 'settings/tokens';
import { READING_MEASURE } from 'constants/layout';
import { SectionLabel } from 'components/UI/SectionLabel';
import { TermInfo } from 'components/UI/TermInfo';
import { sanitizeProductDescription } from 'utils/certificateHtml';
import { isCertificateFieldHidden } from '@repo/types';

interface ProductTabContentProps {
  certificate: any;
  showCurrentOwner?: boolean;
  /**
   * Rendered between the facts and the description.
   *
   * The owner view puts the Provenance / Scans / Activity rows here rather than
   * after this block, which is where they first landed. A product with real
   * marketing copy pushed them off the bottom of the page, so the three screens
   * the certificate can reach were only findable by scrolling past prose —
   * Chrono24 puts its chevron rows immediately after the spec table for exactly
   * this reason. The public stack passes nothing.
   */
  afterFacts?: React.ReactNode;
}

export const ProductTabContent: React.FC<ProductTabContentProps> = ({
  certificate,
  showCurrentOwner = true,
  afterFacts,
}) => {
  const ownerData = getOwner(
    certificate?.customer,
    certificate?.company,
    certificate?.transfer?.type,
    // VERA-304: while a transfer is still pending the payload carries no `customer`,
    // so the sender of that transfer is who currently owns it. See `getOwner`.
    certificate?.transfer?.fromCustomer
  );
  const productUrl = certificate?.product?.product_url;
  const description = String(certificate?.product?.description || '');
  /*
   * VRG-788 — the brand may hide the description per SKU. Hidden means the whole block
   * goes, LABEL AND ALL: the "No description provided" empty state exists to tell an
   * owner apart "nothing written" from "not loaded", and showing it for a field the
   * brand deliberately withheld would report a gap in their data instead.
   */
  const descriptionHidden = isCertificateFieldHidden(
    certificate?.product?.hidden_certificate_fields,
    'description'
  );

  return (
    <Stack spacing={2} sx={{ pt: 3 }}>
      {/*
        DETAILS — everything that describes the OBJECT, in one labelled group.

        The description used to render AFTER the History rows, which put a
        paragraph about the product below the navigation to its provenance and
        scans. It is a fact about the thing, not something that follows from its
        history, so it belongs with the other facts.

        And the group is labelled, which is the change that makes HISTORY stop
        looking arbitrary: one caption among unlabelled content reads as a
        mistake, three captions read as structure.
      */}
      <SectionLabel first>Details</SectionLabel>

      {/* `ownedSince` only in the owner's own view — `showCurrentOwner` is false
          exactly there, so the two are the same question answered two ways: a
          visitor gets the owner BLOCK (identity is the information), the owner
          gets the DATE as a fact (identity is not). */}
      <ProductDetailsPanel
        certificate={certificate}
        ownedSince={
          showCurrentOwner ? null : certificate?.date_of_ownership || certificate?.created_at
        }
      />

      {/* VERA-315: rendered ONLY when there is a URL to open.
          It used to render always and sit disabled when the product had no link,
          which is a control the user can never enable — on the seeded catalogue
          that was 9 of 11 products showing a dead button. A missing link is
          absence, and absence is best said by showing nothing.

          (The reason it was disabled even on products that DO have a URL was a
          backend bug, fixed alongside this: the certificate aggregate query never
          selected `products.product_url`, so it arrived null regardless. See
          certificate.drizzle.repository.ts.) */}
      {productUrl && (
        <ButtonUI
          variant='secondary'
          startIcon={<ExternalLink size={18} />}
          onClick={() => window.open(productUrl, '_blank', 'noopener,noreferrer')}
          sx={{ paddingX: 2 }}
        >
          View product
        </ButtonUI>
      )}

      {/* VERA-315: the description is a FIELD, not an accordion.
          It was a collapsible section that defaulted to open, so the chevron's
          only real function was to let you hide the page's one piece of prose —
          and it cost an indent (the body was pushed in 28px to clear the chevron)
          plus a heading that greyed out when open, which read as disabled.

          Now: a heading and the text, always rendered, at the left margin like
          everything else. The empty case says so rather than collapsing to
          nothing, so an owner can tell "no description" from "not loaded". */}
      {!descriptionHidden && (
        <Stack>
          {/*
            A FIELD label, not a section heading.

            At `font.body` weight 500 it sat at the same visual level as DETAILS and
            HISTORY, so the page appeared to have four peers — three real groups and
            a paragraph pretending to be one. Description is a product FIELD, like
            Certificate ID above it, so it takes the field-label treatment: smaller,
            secondary, and clearly subordinate to the caption it now sits under.
          */}
          <Typography sx={{ fontSize: font.label, color: 'text.secondary', mb: `${space.sm}px` }}>
            Description
          </Typography>
          {description ? (
            <Typography
              component='div'
              sx={{
                fontSize: font.body,
                lineHeight: 1.5,
                /*
                  THE reading measure, on the PROSE rather than on a container.

                  This is the page's one run of real prose, and it was taking the
                  record column's full width — 99 characters per line after the
                  content widths collapsed to one, up from 86 before it. Both are
                  past the 45–75 band, so the old container cap had already been
                  failing at the job it was justified by; widening it only made a
                  pre-existing miss visible.

                  `READING_MEASURE` rather than a number typed here: 512 is derived
                  from the narrowest face a tenant can pick in Studio, so no brand's
                  theme can push this line past the band. The derivation, the
                  per-font measurements, and why `ch` measured WORSE than px are all
                  in that constant.

                  Left-aligned, not centred — it sits in a column of left-aligned
                  label/value rows, and centring the one capped element in an
                  uncapped column is what makes a measure read as an indent.
                */
                maxWidth: `${READING_MEASURE}px`,
                '& p': { m: 0 },
                '& p + p': { mt: `${space.md}px` },
                '& ul, & ol': { my: `${space.md}px`, pl: 3 },
                '& img': { maxWidth: '100%', height: 'auto' },
              }}
              /*
                SANITISED as of VRG-104, and this was a pre-existing hole rather than
                something the custom-field work introduced: the description is
                brand-authored (often IMPORTED verbatim from a connected store) and was
                being set as `innerHTML` with no filtering at all. The certificate app is
                one origin shared by every brand and it carries the owner's session, so
                markup that executes here is not scoped to the tenant that wrote it.

                Fixed in this change because the sanitizer now exists for the block below
                — see utils/certificateHtml for the two allow-lists and why a description
                keeps its links and images while a custom field does not.
              */
              dangerouslySetInnerHTML={{ __html: sanitizeProductDescription(description) }}
            />
          ) : (
            <Typography sx={{ fontSize: font.body, lineHeight: 1.5, color: 'text.secondary' }}>
              No description provided for this product
            </Typography>
          )}
        </Stack>
      )}

      {/* VRG-104 — the brand's bounded slot: their `rich` and `link` custom fields, in
          their order, below the description and above the History rows. `text` fields
          are not here; they are rows in the facts table above. See BrandCustomFields for
          why this is a slot in a fixed spine rather than a per-SKU canvas. */}
      <BrandCustomFields fields={certificate?.product?.custom_fields} />

      {afterFacts}

      {showCurrentOwner && (
        <Stack id='current-owner-section'>
          {/* VRG-418 — the `(i)` explains OWNER, which is the load-bearing word here: to
              a visitor who has just tapped someone else's item, "owner" means a person
              recorded by a completed transfer rather than whoever is holding it, and
              nothing else on this page says so. The glyph sits beside the heading because
              that is the word it defines. */}
          {/*
              A SECTION caption, not a field label -- and this is the same correction
              the Description label got a few lines up, applied in the other direction.

              It was `font.body` weight 500, which is precisely the treatment that
              docblock demotes Description OUT of for making a field look like a peer
              of DETAILS. So the page carried three visual levels where it has two
              kinds of thing: DETAILS as a real caption, the owner block as a
              half-caption, and the field labels below. Half-captions are the
              "one caption among unlabelled content reads as a mistake" case that
              `SectionLabel`'s own note warns about, seen from the other side.

              Renamed with it. "Current owner" over a card whose first line is the
              owner's name and whose second is "Owner since <date>" says the word
              three times; OWNERSHIP names the GROUP, which is what a section caption
              is for, and the card keeps saying which owner and since when.

              `inline` drops SectionLabel's own 32/8 margins so this row carries them
              instead -- and they need `&&` because the parent Stack's `spacing`
              compiles to `& > :not(style) + :not(style)`, which outranks a plain
              child class and would otherwise pin the top gap to the Stack's 16px.
          */}
          <Stack
            direction='row'
            alignItems='center'
            spacing={0.5}
            sx={{ '&&': { mt: 4, mb: 1 } }}
          >
            <SectionLabel inline>Ownership</SectionLabel>
            <TermInfo termId='owner' />
          </Stack>
          <OwnerProvenanceCard
            name={ownerData?.title}
            subtext={ownerData?.subtext}
            avatarUrl={ownerData?.image}
            /* The holder's KIND, so a company's brand mark is drawn as a logo and not as
               a face. `getOwner` has always returned this; this call site simply never
               forwarded it, so `ownerType` fell to its 'customer' default and every
               company-held certificate rendered its wordmark in a 9999px circle under
               `object-fit: cover`. Measured on the DAC mark (98x59, aspect 1.661) in a
               40px circle: cover scales it to 66.4px wide and crops 13.2px off EACH
               side -- 27% of the signature lost from both ends. That is the exact
               failure `EntityAvatar`'s docblock names ("slices the ends off a
               wordmark"), and it was invisible because a symmetric crop reads as a
               mark that is merely badly centred. */
            ownerType={ownerData?.type}
            sinceDate={certificate?.updated_at || certificate?.created_at}
            completionMethod={certificate?.transfer?.completion_method ?? null}
          />
        </Stack>
      )}

    </Stack>
  );
};
